import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Check, ArrowRight, Info } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CameraScreen } from '../../components/CameraScreen';
export function ManutenzioneBeforePhotosPage() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<string[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const minPhotos = 2; // Minimo 2 foto richieste
  const maxPhotos = 4; // Massimo 4 foto
  const isComplete = photos.length >= minPhotos;
  const progress = photos.length / minPhotos * 100;
  const handleOpenCamera = () => {
    if (photos.length < maxPhotos) {
      setCameraOpen(true);
    }
  };
  const handleCapturePhoto = (photoUrl: string) => {
    setPhotos(prev => [...prev, photoUrl]);
    setCameraOpen(false);
  };
  const handleCancelCamera = () => {
    setCameraOpen(false);
  };
  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };
  const handleStartIntervention = () => {
    if (isComplete) {
      navigate('/manutenzione/intervention/property-123');
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1350">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-1351">
        {/* Page Header */}
        <div className="mb-12" data-id="element-1352">
          <h1 className="text-4xl font-bold text-navy mb-3" data-id="element-1353">Foto del Prima</h1>
          <p className="text-base text-gray-600" data-id="element-1354">
            Via Roma 123, Appartamento 4B • Bagno principale
          </p>
        </div>

        {/* Progress Section */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-12" data-id="element-1355">
          <div className="flex justify-between items-end mb-4" data-id="element-1356">
            <span className="text-base font-medium text-gray-500" data-id="element-1357">
              Documenta lo stato attuale del problema
            </span>
            <span className="text-xl font-bold text-teal-deep" data-id="element-1358">
              {photos.length} / {minPhotos} foto
            </span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden" data-id="element-1359">
            <motion.div className="h-full bg-teal-deep rounded-full" initial={{
            width: 0
          }} animate={{
            width: `${Math.min(progress, 100)}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-1360" />
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-1361">
          {/* Existing Photos */}
          {photos.map((photo, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="relative" data-id="element-1362">
              <Card className="relative aspect-[4/3] overflow-hidden border-2 border-teal-deep shadow-md group" data-id="element-1363">
                <img src={photo} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" data-id="element-1364" />
                <div className="absolute top-2 right-2 bg-teal-deep text-white p-2 rounded-full shadow-lg" data-id="element-1365">
                  <Check className="w-5 h-5" data-id="element-1366" />
                </div>
                <button onClick={() => handleRemovePhoto(index)} className="absolute top-2 left-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1367">
                  <span className="text-sm font-bold" data-id="element-1368">✕</span>
                </button>
              </Card>
              <p className="text-center font-medium text-base text-gray-700 mt-2" data-id="element-1369">
                Foto {index + 1}
              </p>
            </motion.div>)}

          {/* Add Photo Slots */}
          {photos.length < maxPhotos && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={handleOpenCamera} className="cursor-pointer" data-id="element-1370">
              <Card className="relative aspect-[4/3] overflow-hidden bg-white border-2 border-dashed border-gray-300 hover:border-teal-deep hover:bg-teal-50/30 transition-all group" data-id="element-1371">
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 group-hover:text-teal-deep transition-colors" data-id="element-1372">
                  <Camera className="w-12 h-12 mb-3" data-id="element-1373" />
                  <span className="text-base font-medium" data-id="element-1374">Scatta Foto</span>
                </div>
              </Card>
              <p className="text-center font-medium text-base text-gray-500 mt-2" data-id="element-1375">
                {photos.length < minPhotos ? 'Richiesta' : 'Opzionale'}
              </p>
            </motion.div>}
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6" data-id="element-1376">
          <div className="flex items-start gap-3" data-id="element-1377">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1378" />
            <div data-id="element-1379">
              <p className="text-base text-blue-900 font-semibold mb-2" data-id="element-1380">
                💡 Suggerimenti per le foto
              </p>
              <ul className="text-base text-blue-900 space-y-1" data-id="element-1381">
                <li data-id="element-1382">• Scatta da diverse angolazioni</li>
                <li data-id="element-1383">• Assicurati che il problema sia ben visibile</li>
                <li data-id="element-1384">• Usa una buona illuminazione</li>
                <li data-id="element-1385">• Minimo {minPhotos} foto richieste</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40" data-id="element-1386">
        <div className="max-w-4xl mx-auto" data-id="element-1387">
          <Button onClick={handleStartIntervention} disabled={!isComplete} className={`w-full text-lg h-14 ${!isComplete ? 'opacity-40 cursor-not-allowed' : ''}`} data-id="element-1388">
            Avvia Intervento
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-1389" />
          </Button>
        </div>
      </div>

      {/* Camera Screen */}
      <CameraScreen isOpen={cameraOpen} roomName="Bagno principale" referencePhotoUrl="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80" onCapture={handleCapturePhoto} onCancel={handleCancelCamera} data-id="element-1390" />
    </div>;
}